import { DownOutlined, ExclamationCircleOutlined, PrinterOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Col, Descriptions, Dropdown, Empty, Flex, Form, Input, Modal, Radio, Result, Row, Select, Skeleton, Space, Table, Tag, Timeline, Tooltip, Typography } from "antd";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { ApiError, api, unwrap, type OrderDetail as Detail } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useTabTitle } from "@/components/tabs/WorkTabs";
import { RefreshButton } from "@/components/RefreshButton";
import { useI18n, type MessageKey } from "@/i18n";
import { SkuThumb } from "@/components/SkuThumb";
import { useWarehouses } from "@/lib/useWarehouses";
import { PlatformLabel } from "../channels/PlatformBadge";
import { HoldTag, OrderStateTag, OrderStatusTag, usePlatformStatus } from "./OrderStatusTag";
import { ArrangeModal, useLabelPrinter, type ShipTarget } from "./shipping";
import { CaseProgressTag } from "../aftersales/common";
import { OrderCases } from "../aftersales/OrderCases";
import { ParcelCard } from "./ParcelCard";
import { ManualPanel } from "./ManualPanel";
import { ShipBy } from "./ShipBy";
import { ManualLinesModal, SwapLineModal } from "./OrderEdit";
import { MapModal } from "../catalog/mapping";
import { CourierKindTag } from "./CourierKind";
import { TrackingHistory, trackable } from "./Tracking";
import { OrderMoney } from "./OrderMoney";

type Line = Detail["lines"][number];
type Pkg = Detail["packages"][number];
type CancelReason = "out_of_stock" | "customer_request" | "other";

/**
 * Detail order (App Flow §6), opened as its own work tab named after the order
 * number, so several orders can be compared side by side.
 */
export function OrderDetail() {
  const { orderID = "" } = useParams();
  return <OrderDetailView orderID={orderID} />;
}

/** The order, in its own tab or in the order list's side panel (inPanel: no tab title). */
export function OrderDetailView({ orderID, inPanel }: { orderID: string; inPanel?: boolean }) {
  const navigate = useNavigate();
  const { t, tDynamic, formatDateTime, formatMoney } = useI18n();
  const platformStatus = usePlatformStatus();
  const { me, can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const warehouses = useWarehouses();
  const tz = me?.workspace?.timezone;
  const manage = can("orders.manage");
  const [cancelling, setCancelling] = useState(false);
  const [arranging, setArranging] = useState<ShipTarget[] | null>(null);
  const printer = useLabelPrinter();
  const [form] = Form.useForm<{ reason: CancelReason }>();
  const [holding, setHolding] = useState(false);
  const [swapping, setSwapping] = useState<Line | null>(null);
  // Platform SKU being mapped from an unmapped line ("Petakan"); every open order follows.
  const [mapping, setMapping] = useState<string[] | null>(null);
  const [editingLines, setEditingLines] = useState(false);
  const [holdNote, setHoldNote] = useState("");
  const [moving, setMoving] = useState(false);
  const [moveTo, setMoveTo] = useState<string | undefined>();

  const detail = useQuery({
    queryKey: ["orders", "detail", orderID],
    queryFn: () => unwrap(api.GET("/v1/orders/{orderID}", { params: { path: { orderID } } })),
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
  });
  useTabTitle(inPanel ? undefined : detail.data?.order.externalId);
  // The order's aftersales case at a glance (the card below lists them all).
  const cases = useQuery({
    queryKey: ["aftersales", "order", orderID],
    queryFn: () => unwrap(api.GET("/v1/aftersales", { params: { query: { orderId: orderID } } })),
  });
  const caseNow =
    cases.data?.items.find((x) => x.status === "open" || x.status === "receiving" || x.status === "received") ??
    cases.data?.items.find((x) => x.status !== "cancelled");

  const done = (text: MessageKey) => {
    void message.success(t(text));
    void queryClient.invalidateQueries({ queryKey: ["orders"] });
    void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    void queryClient.invalidateQueries({ queryKey: ["inventory"] });
  };
  const fail = (err: unknown) => void message.error(errorText(err));
  // "Perbarui dari marketplace": the order read again now.
  const refreshOrder = () =>
    unwrap(api.POST("/v1/orders/{orderID}/refresh", { params: { path: { orderID } } })).then(
      (res) => {
        queryClient.setQueryData(["orders", "detail", orderID], res);
        void queryClient.invalidateQueries({ queryKey: ["orders"] });
        void queryClient.invalidateQueries({ queryKey: ["aftersales"] });
        void message.success(t("orders.refreshed"));
      },
      (err: unknown) => {
        fail(err);
        throw err;
      },
    );
  const path = { params: { path: { orderID } } };

  const process = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/orders/{orderID}/process", path)),
    onSuccess: () => done("orders.processed"),
    onError: fail,
  });
  const hold = useMutation({
    mutationFn: (note: string) => unwrap(api.POST("/v1/orders/{orderID}/hold", { ...path, body: { note } })),
    onSuccess: () => {
      setHolding(false);
      setHoldNote("");
      done("orders.heldDone");
    },
    onError: fail,
  });
  const release = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/orders/{orderID}/release", path)),
    onSuccess: () => done("orders.releasedDone"),
    onError: fail,
  });
  const move = useMutation({
    mutationFn: (warehouseId: string) => unwrap(api.PUT("/v1/orders/{orderID}/warehouse", { ...path, body: { warehouseId } })),
    onSuccess: () => {
      setMoving(false);
      done("orders.movedDone");
    },
    onError: fail,
  });
  // zerpid: the store left Zerpid, the order is closed here only.
  const cancel = useMutation({
    mutationFn: ({ reason, scope }: { reason: CancelReason; scope: "marketplace" | "zerpid" }) =>
      unwrap(api.POST("/v1/orders/{orderID}/cancel", { ...path, body: { reason, scope } })),
    onSuccess: (_, v) => {
      setCancelling(false);
      done(v.scope === "zerpid" ? "orders.cancelZerpidDone" : "orders.cancelDone");
    },
    onError: fail,
  });
  const respond = useMutation({
    mutationFn: (accept: boolean) => unwrap(api.POST("/v1/orders/{orderID}/cancellation-response", { ...path, body: { accept } })),
    onSuccess: () => done("orders.responded"),
    onError: fail,
  });

  if (detail.isError) {
    return detail.error instanceof ApiError && detail.error.status === 404 ? (
      <Result status="404" title={t("page.notFound.title")} />
    ) : (
      <Result status="error" title={errorText(detail.error)} extra={<Button onClick={() => void detail.refetch()}>{t("common.retry")}</Button>} />
    );
  }
  const d = detail.data;
  if (!d) return <Skeleton active />;
  const o = d.order;
  const manual = o.platform === "manual";
  const cancellable = ["awaiting_payment", "new", "processing"].includes(o.status) && !o.cancelRequested;
  // The store left Zerpid: nothing reaches its marketplace any more.
  const storeGone = o.storeStatus === "disconnected";
  const platformName = t(`platform.${o.platform}`);
  const arrangeable = !manual && ["new", "processing"].includes(o.status) && !o.shippingArranged && !o.cancelRequested;
  const printable = o.shippingArranged && !["cancelled", "returned"].includes(o.status);
  const target: ShipTarget = { id: o.id, externalId: o.externalId, platform: o.platform };
  const lbl = d.labels[0];
  const busy = process.isPending || cancel.isPending || respond.isPending || hold.isPending || release.isPending || move.isPending;
  // The seller may still hold the order back or move it while the warehouse
  // has not started on it.
  const open = o.status === "new" || o.status === "processing";

  // Wave: the picking method; label: how many times it was printed.
  const stepDetail = (kind: string, detail: string) => {
    if (kind === "wave") return tDynamic(`wms.method.${detail}`, "orders.history.system");
    if (kind === "label_printed") return t("orders.step.prints", { n: detail });
    return detail;
  };

  const noteText = (note: string) => {
    // Wave, packing and handover show as warehouse steps beside the status.
    if (!note || /^(wave|packed|handover):/.test(note)) return "";
    const i = note.indexOf(":");
    const head = i < 0 ? note : note.slice(0, i);
    const tail = i < 0 ? "" : note.slice(i + 1);
    const base = tDynamic(`orders.note.${head}`, "orders.history.system");
    if (!tail) return base;
    if (head === "held" || head === "released") return `${base}: ${tDynamic(`orders.hold.${tail}`, "orders.reason.other")}`;
    if (head === "warehouse_changed" || head === "line_swapped") return `${base}: ${tail}`;
    return `${base}: ${tDynamic(`orders.reason.${tail}`, "orders.reason.other")}`;
  };

  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Space align="center" size={12} wrap>
          <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }} className="z-num">
            {o.externalId}
          </Typography.Title>
          <OrderStateTag o={o} />
          {o.platformStatus && <Typography.Text type="secondary">{platformStatus(o.platformStatus)}</Typography.Text>}
          {caseNow && <CaseProgressTag c={caseNow} onClick={() => navigate(`/app/aftersales/${caseNow.id}`)} />}
          <PlatformLabel platform={o.platform} />
          <Typography.Text type="secondary">{o.shopName}</Typography.Text>
        </Space>
        <Space wrap>
          {!manual && <RefreshButton label={t("orders.refreshFromMarketplace")} onRefresh={refreshOrder} />}
          {manage && (
            <Space wrap>
              {printable && (
                <Space.Compact>
                  <Button icon={<PrinterOutlined />} onClick={() => printer.print([target])}>
                    {t("orders.label")}
                  </Button>
                  {lbl?.source !== "zerpid" && (
                    <Dropdown
                      trigger={["click"]}
                      menu={{
                        items: [{ key: "original", label: t("orders.printOriginal"), title: t("orders.printOriginalHelp") }],
                        onClick: () => printer.print([target], { original: true }),
                      }}
                    >
                      <Button icon={<DownOutlined />} aria-label={t("orders.printOriginal")} />
                    </Dropdown>
                  )}
                </Space.Compact>
              )}
              {cancellable && (
                <Button danger disabled={busy} onClick={() => setCancelling(true)}>
                  {t("orders.cancel")}
                </Button>
              )}
              {arrangeable && (
                // TikTok ON_HOLD: no package to ship yet; Zerpid books it as soon as it can.
                <Tooltip title={o.platformStatus === "ON_HOLD" ? t("orders.arrangeOnHold") : undefined}>
                  <Button
                    type={o.status === "processing" ? "primary" : "default"}
                    disabled={busy || o.platformStatus === "ON_HOLD"}
                    onClick={() => setArranging([target])}
                  >
                    {t("orders.arrange")}
                  </Button>
                </Tooltip>
              )}
              {open && o.hold !== "manual" && o.hold !== "rule" && (
                <Button disabled={busy} onClick={() => setHolding(true)}>
                  {t("orders.holdAction")}
                </Button>
              )}
              {open && (
                <Button
                  disabled={busy}
                  onClick={() => {
                    setMoveTo(o.warehouseId ?? undefined);
                    setMoving(true);
                  }}
                >
                  {t("orders.moveWarehouse")}
                </Button>
              )}
            </Space>
          )}
        </Space>
      </Flex>

      {(o.splitCombine || o.combineGroup || o.combinedWith.length > 0) && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          title={t(o.splitCombine === "SPLIT" ? "orders.splitTag" : "orders.combinedTag")}
          description={
            <>
              {t(o.splitCombine === "SPLIT" ? "orders.splitHelp" : "orders.combinedHelp")}
              {o.combinedWith.length > 0 && (
                <>
                  {" "}
                  {t("orders.combinedWith")}{" "}
                  {o.combinedWith.map((c, i) => (
                    <span key={c.id}>
                      {i > 0 && ", "}
                      <Link to={`/app/orders/${c.id}`} className="z-num">
                        {c.externalId}
                      </Link>
                    </span>
                  ))}
                </>
              )}
            </>
          }
        />
      )}

      {o.cancelRequested && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          title={t("orders.cancelRequestTitle")}
          description={t("orders.cancelRequestBody")}
          action={
            manage && (
              <Space orientation="vertical">
                <Button danger type="primary" block loading={respond.isPending && respond.variables} disabled={busy} onClick={() => respond.mutate(true)}>
                  {t("orders.acceptCancel")}
                </Button>
                <Button block loading={respond.isPending && !respond.variables} disabled={busy} onClick={() => respond.mutate(false)}>
                  {t("orders.rejectCancel")}
                </Button>
              </Space>
            )
          }
        />
      )}
      {o.hold && o.hold !== "cancel_request" && o.hold !== "unmapped" && (
        <Alert
          type={o.hold === "rule" || o.hold === "manual" ? "info" : "warning"}
          showIcon
          style={{ marginBottom: 16 }}
          title={
            <Space size={6} wrap>
              <HoldTag hold={o.hold} />
              {o.heldSince && <Typography.Text type="secondary">{t("orders.heldSince", { at: formatDateTime(o.heldSince, tz) })}</Typography.Text>}
            </Space>
          }
          description={
            <Space orientation="vertical" size={2}>
              <Typography.Text>{t(`orders.holdHelp.${o.hold}`)}</Typography.Text>
              {o.holdNote && <Typography.Text strong>{o.holdNote}</Typography.Text>}
            </Space>
          }
          action={
            manage &&
            o.hold !== "unpaid" &&
            (o.hold === "rule" || o.hold === "manual" || o.hold === "combined" ? (
              <Button type="primary" loading={release.isPending} disabled={busy} onClick={() => release.mutate()}>
                {t("orders.release")}
              </Button>
            ) : (
              <Button type="primary" loading={process.isPending} disabled={busy} onClick={() => process.mutate()}>
                {o.hold === "arrange_failed" ? t("orders.retryArrange") : t("orders.processAgain")}
              </Button>
            ))
          }
        />
      )}
      {storeGone && ["awaiting_payment", "new", "processing", "packed"].includes(o.status) && (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("orders.storeGoneTitle", { shop: o.shopName })} description={t("orders.storeGoneBody")} />
      )}
      {/* Mapping a line now changes only orders still to be worked on. */}
      {["awaiting_payment", "new", "processing", "packed"].includes(o.status) && d.lines.some((l) => !l.internalSkuId && !l.cancelled) && (
        <Alert type="warning" showIcon icon={<ExclamationCircleOutlined />} style={{ marginBottom: 16 }} title={t("orders.unmapped")} description={t("orders.unmappedHelp")} />
      )}
      {o.outside && <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("orders.outside")} description={t("orders.outsideBody")} />}

      <Row gutter={[16, 16]}>
        <Col xs={24} xl={16}>
          <Space orientation="vertical" size={16} style={{ width: "100%" }}>
            <Card
              title={t("orders.lines")}
              size="small"
              extra={manual && open && manage && <Button size="small" onClick={() => setEditingLines(true)}>{t("orders.edit.lines")}</Button>}
            >
              <Table<Line>
                size="small"
                rowKey="id"
                pagination={false}
                dataSource={d.lines}
                scroll={{ x: 600 }}
                columns={[
                  {
                    title: t("common.name"),
                    dataIndex: "name",
                    render: (v: string, l) => (
                      <Space align="start" style={{ paddingLeft: l.role === "component" ? 24 : 0 }}>
                      <SkuThumb skuId={l.internalSkuId} />
                      <Space orientation="vertical" size={0}>
                        <Typography.Text delete={l.cancelled}>{v}</Typography.Text>
                        <Space size={4} wrap>
                          <Typography.Text type="secondary" className="z-num" style={{ fontSize: 12 }}>
                            {l.sellerSku || l.skuId}
                          </Typography.Text>
                          {l.role === "bundle" && <Tag color="purple">{t("orders.line.bundle")}</Tag>}
                          {l.role === "component" && <Tag>{t("orders.line.component")}</Tag>}
                          {l.role === "gift" && <Tag color="magenta">{t("orders.line.gift")}</Tag>}
                          {l.cancelled && <Tag>{t("orders.lineCancelled")}</Tag>}
                          {!l.internalSkuId && !l.cancelled && <Tag color="warning">{t("orders.unmapped")}</Tag>}
                          {l.skuOverride && !l.cancelled && <Tag color="blue">{t("orders.edit.switchedTo", { code: l.internalSkuCode ?? "" })}</Tag>}
                          {!l.skuOverride && l.internalSkuCode && l.internalSkuCode.toUpperCase() !== (l.sellerSku || "").toUpperCase() && (
                            <Typography.Text type="secondary" className="z-num" style={{ fontSize: 12 }}>
                              → {l.internalSkuCode}
                            </Typography.Text>
                          )}
                          {!l.internalSkuId && !l.cancelled && l.platformSkuId && can("catalog.manage") && (
                            <Typography.Link style={{ fontSize: 12 }} onClick={() => setMapping([l.platformSkuId!])}>
                              {t("catalog.map")}
                            </Typography.Link>
                          )}
                          {open && manage && !l.cancelled && (l.role === "item" || l.role === "bundle") && (
                            <Typography.Link style={{ fontSize: 12 }} onClick={() => setSwapping(l)}>
                              {t("orders.edit.swap")}
                            </Typography.Link>
                          )}
                        </Space>
                        {l.note && (
                          <Typography.Text type={l.note === "gift_out_of_stock" ? "warning" : "secondary"} style={{ fontSize: 12 }}>
                            {l.note === "gift_out_of_stock" ? t("orders.lineNote.gift_out_of_stock") : l.note}
                          </Typography.Text>
                        )}
                      </Space>
                      </Space>
                    ),
                  },
                  { title: t("orders.qty"), dataIndex: "quantity", width: 70, align: "right", render: (v: number) => <span className="z-num">{v}</span> },
                  {
                    title: t("orders.unitPrice"),
                    dataIndex: "unitPrice",
                    width: 140,
                    align: "right",
                    render: (v: number) => <span className="z-num">{formatMoney(v, o.currency)}</span>,
                  },
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={2}>
                      <Typography.Text strong>{t("orders.total")}</Typography.Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align="right">
                      <Typography.Text strong className="z-num">
                        {formatMoney(o.totalAmount, o.currency)}
                      </Typography.Text>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            </Card>

            <Card title={t("orders.packages")} size="small">
              {d.packages.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("orders.noPackages")} />
              ) : (
                <Table<Pkg>
                  size="small"
                  rowKey="externalId"
                  pagination={false}
                  dataSource={d.packages}
                  columns={[
                    { title: "ID", dataIndex: "externalId", render: (v: string) => <span className="z-num">{v}</span> },
                    { title: t("orders.carrier"), dataIndex: "carrier", render: (v: string) => v || "—" },
                    {
                      title: t("orders.trackingNo"),
                      dataIndex: "trackingNo",
                      render: (v: string) =>
                        v ? (
                          <Typography.Text strong className="z-num" copyable>
                            {v}
                          </Typography.Text>
                        ) : (
                          <Typography.Text type="secondary">{t("orders.trackingPending")}</Typography.Text>
                        ),
                    },
                    { title: t("orders.platformStatus"), dataIndex: "status" },
                  ]}
                />
              )}
              {trackable(o.platform) && d.packages.some((p) => p.trackingNo) && (
                <div style={{ marginTop: 12 }}>
                  <TrackingHistory orderId={o.id} tz={tz} />
                </div>
              )}
              {lbl && (
                <Space orientation="vertical" size={2} style={{ marginTop: 12 }}>
                  <Space size={8}>
                    <Typography.Text type="secondary">{t("orders.labelStatus")}</Typography.Text>
                    {lbl.printCount > 0 ? <Tag color="success">{t("orders.labelPrinted", { n: lbl.printCount })}</Tag> : <Tag>{t("orders.labelNotPrinted")}</Tag>}
                  </Space>
                  {lbl.lastPrintedAt && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {t("orders.lastPrinted", { at: formatDateTime(lbl.lastPrintedAt, tz), name: lbl.lastPrintedBy })}
                    </Typography.Text>
                  )}
                  {lbl.source === "zerpid" && (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {t("orders.labelSourceZerpid")}
                    </Typography.Text>
                  )}
                </Space>
              )}
            </Card>
            {warehouses.data?.items.find((w) => w.id === o.warehouseId)?.wmsEnabled && <ParcelCard orderId={o.id} />}
            {(o.status === "shipped" || o.status === "completed" || o.status === "returned") && (
              <OrderCases order={{ id: o.id, externalId: o.externalId, warehouseId: o.warehouseId, lines: d.lines }} />
            )}
          </Space>
        </Col>

        <Col xs={24} xl={8}>
          <Space orientation="vertical" size={16} style={{ width: "100%" }}>
            {manual && <ManualPanel o={o} tracking={d.packages.find((p) => p.trackingNo)?.trackingNo ?? ""} />}
            {trackable(o.platform) && can("finance.view") && <OrderMoney orderId={o.id} tz={tz} />}
            <Card size="small">
              <Descriptions column={1} size="small">
                <Descriptions.Item label={t("orders.orderedAt")}>{o.orderedAt ? formatDateTime(o.orderedAt, tz) : "—"}</Descriptions.Item>
                <Descriptions.Item label={t("orders.buyer")}>{o.buyerName || "—"}</Descriptions.Item>
                <Descriptions.Item label={t("orders.recipient")}>
                  {o.recipientName || "—"}
                  {o.recipientPhone && (
                    <Typography.Text type="secondary" className="z-num">
                      {" "}
                      · {o.recipientPhone}
                    </Typography.Text>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={t("orders.address")}>{[o.address, o.city, o.postalCode].filter(Boolean).join(", ") || "—"}</Descriptions.Item>
                <Descriptions.Item label={t("orders.payment")}>{o.paymentMethod || "—"}</Descriptions.Item>
                <Descriptions.Item label={t("orders.carrier")}>
                  <Space size={6} wrap>
                    {o.courier || o.carrier || "—"}
                    {o.courier && o.courier !== o.carrier && <Typography.Text type="secondary">({o.carrier})</Typography.Text>}
                    <CourierKindTag kind={o.courierKind} />
                  </Space>
                </Descriptions.Item>
                <Descriptions.Item label={t("orders.shipBy")}>
                  <ShipBy at={o.shipBy} done={!["new", "processing"].includes(o.status)} />
                </Descriptions.Item>
                {!manual && (
                  <Descriptions.Item label={t("orders.platformStatus")}>
                    <span>{platformStatus(o.platformStatus)}</span>
                    <Typography.Text type="secondary" className="z-num" style={{ fontSize: 12 }}>
                      {" "}
                      ({o.platformStatus})
                    </Typography.Text>
                  </Descriptions.Item>
                )}
                <Descriptions.Item label={t("orders.warehouse")}>
                  <Space size={4} wrap>
                    {warehouses.nameOf(o.warehouseId) ?? "—"}
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      · {t(`orders.warehouseSource.${o.warehouseSource}`)}
                    </Typography.Text>
                  </Space>
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card title={t("orders.history")} size="small">
              <Timeline
                items={[
                  // Status changes and what the warehouse did, in time order.
                  ...d.history.map((h) => ({
                    at: h.at,
                    key: h.at + h.toStatus + h.note,
                    color: undefined as string | undefined,
                    content: (
                      <Space orientation="vertical" size={0}>
                        <Space size={6} wrap>
                          <OrderStatusTag status={h.toStatus} />
                          {h.note && <Typography.Text>{noteText(h.note)}</Typography.Text>}
                          {h.source === "marketplace" && h.platformStatus && (
                            <Typography.Text type="secondary">{platformStatus(h.platformStatus)}</Typography.Text>
                          )}
                        </Space>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {formatDateTime(h.at, tz)} · {h.source === "marketplace" ? t("orders.history.marketplace") : h.actorName || t(`orders.history.${h.source}`)}
                        </Typography.Text>
                      </Space>
                    ),
                  })),
                  ...d.steps.map((s) => ({
                    at: s.at,
                    key: s.kind + s.at,
                    color: "gray" as string | undefined,
                    content: (
                      <Space orientation="vertical" size={0}>
                        <Typography.Text>
                          {t(`orders.step.${s.kind}`)}
                          {s.ref && (
                            <>
                              {" "}
                              <span className="z-num">{s.ref}</span>
                            </>
                          )}
                          {s.detail && <Typography.Text type="secondary"> · {stepDetail(s.kind, s.detail)}</Typography.Text>}
                        </Typography.Text>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          {formatDateTime(s.at, tz)}
                          {s.actor ? ` · ${s.actor}` : ""}
                        </Typography.Text>
                      </Space>
                    ),
                  })),
                ]
                  .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
                  .map(({ key, color, content }) => ({ key, color, content }))}
              />
            </Card>
          </Space>
        </Col>
      </Row>

      <ArrangeModal targets={arranging} onClose={() => setArranging(null)} />
      <SwapLineModal orderId={o.id} line={swapping} onClose={() => setSwapping(null)} />
      <MapModal ids={mapping} onClose={() => setMapping(null)} />
      {editingLines && <ManualLinesModal orderId={o.id} lines={d.lines} open onClose={() => setEditingLines(false)} />}
      <Modal
        open={holding}
        title={t("orders.holdAction")}
        onCancel={() => setHolding(false)}
        onOk={() => hold.mutate(holdNote.trim())}
        okText={t("orders.holdAction")}
        cancelText={t("common.cancel")}
        confirmLoading={hold.isPending}
      >
        <Typography.Paragraph type="secondary">{t("orders.holdActionHelp")}</Typography.Paragraph>
        <Input.TextArea rows={2} maxLength={300} value={holdNote} onChange={(e) => setHoldNote(e.target.value)} placeholder={t("orders.holdNote")} />
      </Modal>
      <Modal
        open={moving}
        title={t("orders.moveWarehouse")}
        onCancel={() => setMoving(false)}
        onOk={() => moveTo && move.mutate(moveTo)}
        okButtonProps={{ disabled: !moveTo || moveTo === o.warehouseId }}
        okText={t("orders.moveWarehouse")}
        cancelText={t("common.cancel")}
        confirmLoading={move.isPending}
      >
        <Typography.Paragraph type="secondary">{t("orders.moveWarehouseHelp")}</Typography.Paragraph>
        <Select<string>
          value={moveTo}
          onChange={setMoveTo}
          style={{ width: "100%" }}
          options={warehouses.active.map((w) => ({ value: w.id, label: w.name }))}
        />
      </Modal>
      {printer.ui}
      <Modal
        open={cancelling}
        title={t("orders.cancelTitle", { number: o.externalId })}
        okText={storeGone ? t("orders.cancelZerpidOk") : t("orders.cancelAtOk", { platform: platformName })}
        okButtonProps={{ danger: true }}
        cancelText={t("common.close")}
        confirmLoading={cancel.isPending}
        onOk={() => form.submit()}
        onCancel={() => setCancelling(false)}
        destroyOnHidden
      >
        {storeGone ? (
          <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("orders.cancelZerpidTitle")} description={t("orders.cancelZerpidBody", { shop: o.shopName })} />
        ) : (
          <Alert type="error" showIcon style={{ marginBottom: 16 }} title={t("orders.cancelAtTitle", { platform: platformName })} description={t("orders.cancelAtBody", { platform: platformName })} />
        )}
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          initialValues={{ reason: "out_of_stock" }}
          onFinish={(v) => cancel.mutate({ reason: v.reason, scope: storeGone ? "zerpid" : "marketplace" })}
        >
          <Form.Item name="reason" label={t("orders.cancelReason")}>
            <Radio.Group
              options={(["out_of_stock", "customer_request", "other"] as const).map((r) => ({ value: r, label: t(`orders.reason.${r}`) }))}
              style={{ display: "flex", flexDirection: "column", gap: 8 }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
